/**
 * Insight Card — generalized for all four card types
 *
 * Types:
 *   pattern_archetype  (phase 7) — after Phase 3 See, between 3→4
 *   soul_perspective   (phase 8) — mid Phase 4 Shift, after Turn 1
 *   reframe_invitation (phase 9) — mid Phase 4 Shift, after Turn 1 (user picks one)
 *   message_within     (phase 10) — mid Phase 5 Choose, before "I choose..."
 *
 * Full 5-screen Satori-inspired flow:
 * 1. Pre-Card Regulation  — somatic settling (skippable)
 * 2. Arrival              — "An Insight Card has arrived. Just receive."
 * 3. Display              — AI-generated card title + personalized message
 * 4. Post-Card Regulation — somatic anchoring (skippable)
 * 5. Integration          — optional text input, then continue
 *
 * Claude generates: [Title line]\n\n[Message lines]
 * Parsed into cardTitle + cardMessage.
 */
import { useEffect, useRef, useState } from 'react'
import { useScreenId } from '../../lib/useScreenId'
import { BACK_DEPTH, useScreenTrail, useSessionBack } from '../../lib/sessionBack'
import { useTranslation } from 'react-i18next'
import { callClaude } from '../../lib/claude'
import { errorCodeOf, guideErrorKey } from '../../lib/guideErrors'
import { db, supabase } from '../../lib/supabase'
import type { SessionMessage } from '../../types/session'
import LoadingSpiral from '../ui/LoadingSpiral'
import { buildInsightCardCanvas, deliverPng } from '../../lib/shareCard'

export type InsightCardType =
  | 'pattern_archetype'
  | 'soul_perspective'
  | 'reframe_invitation'
  | 'message_within'

interface InsightCardProps {
  cardType?: InsightCardType
  sessionId: string
  userId?: string
  emotion: string
  intensity: number
  firstName?: string
  intentionText?: string
  storyText?: string
  bodyLocation?: string
  reminderText?: string
  patternReflection?: string
  reframeResponse?: string
  seriesContext?: string
  onComplete: () => void
}

type UIState =
  | 'pre-regulation'
  | 'generating'
  | 'display'
  | 'integration'
  | 'error'

/**
 * The card is offered in three phases, so its number depends on which card it
 * is: the pattern card belongs to See (3.C…), the two reframe cards to Shift
 * (4.C…), the message card to Choose (5.C…). See lib/screenIds.ts.
 */
const CARD_HOST = {
  pattern_archetype:  'see',
  soul_perspective:   'shift',
  reframe_invitation: 'shift',
  message_within:     'choose',
} as const satisfies Record<InsightCardType, string>

/** Which of the card's numbered screens each state shows. */
const CARD_SCREEN = {
  'pre-regulation': 'breath',
  // The card is the same screen whether it is still being written or showing.
  'generating':     'card',
  'display':        'card',
  'integration':    'integration',
  'error':          'error',
} as const satisfies Record<UIState, string>

const CARD_META: Record<InsightCardType, {
  phase: number
  gradient: string
}> = {
  pattern_archetype: {
    phase: 7,
    gradient: 'linear-gradient(145deg, #2A1245 0%, #4A2080 100%)',
  },
  soul_perspective: {
    phase: 8,
    gradient: 'linear-gradient(145deg, #0F2040 0%, #1A3A70 100%)',
  },
  reframe_invitation: {
    phase: 9,
    gradient: 'linear-gradient(145deg, #0F2A1A 0%, #1A4A2A 100%)',
  },
  message_within: {
    phase: 10,
    gradient: 'linear-gradient(145deg, #2A1020 0%, #501030 100%)',
  },
}

/** Remove any markdown Claude accidentally includes despite the strict prompt */
function stripMarkdown(text: string): string {
  return text
    .replace(/^#{1,6}\s+/gm, '')           // headers
    .replace(/^[-*]\s+/gm, '')             // bullet list markers
    .replace(/^>\s+/gm, '')               // blockquotes
    .replace(/^\s*---+\s*$/gm, '')         // horizontal rules
    .replace(/\*\*(.+?)\*\*/g, '$1')       // bold
    .replace(/\*(.+?)\*/g, '$1')           // italic asterisk
    .replace(/__(.+?)__/g, '$1')           // bold underscore
    .replace(/_(.+?)_/g, '$1')            // italic underscore
    .replace(/`(.+?)`/g, '$1')            // inline code
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')    // links
    .replace(/[\u{1F300}-\u{1FFFF}]/gu, '') // emoji (unicode ranges)
    .replace(/[💛🌿🌱🕊️✨💫🌸🙏🌀]/g, '') // specific emoji fallback
    .replace(/\n{3,}/g, '\n\n')            // collapse excess blank lines
    .trim()
}

function parseCard(raw: string): { title: string; message: string } {
  const cleaned = stripMarkdown(raw)
  const parts = cleaned.split(/\n\n+/)
  if (parts.length >= 2) {
    return { title: parts[0].trim(), message: parts.slice(1).join('\n\n').trim() }
  }
  const lines = cleaned.split('\n')
  return { title: lines[0].trim(), message: lines.slice(1).join('\n').trim() }
}

function BreathCircle() {
  return (
    <div className="flex items-center justify-center my-6">
      <div style={{ position: 'relative', width: 80, height: 80 }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(155,114,192,0.18) 0%, rgba(155,114,192,0) 70%)',
          animation: 'breathe 4s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute', inset: '16px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(155,114,192,0.35) 0%, rgba(155,114,192,0.1) 70%)',
          animation: 'breathe 4s ease-in-out infinite 0.5s',
        }} />
        <div style={{
          position: 'absolute', inset: '28px', borderRadius: '50%',
          background: '#9B72C0',
          opacity: 0.5,
          animation: 'breathe 4s ease-in-out infinite 1s',
        }} />
      </div>
      <style>{`
        @keyframes breathe {
          0%, 100% { transform: scale(0.92); opacity: 0.6; }
          50% { transform: scale(1.08); opacity: 1; }
        }
      `}</style>
    </div>
  )
}

function CardIllustration({ cardType }: { cardType: InsightCardType }) {
  const accentColor =
    cardType === 'soul_perspective' ? 'rgba(100,160,255,0.3)' :
    cardType === 'reframe_invitation' ? 'rgba(100,200,140,0.3)' :
    cardType === 'message_within' ? 'rgba(220,120,150,0.3)' :
    'rgba(155,114,192,0.25)'

  const strokeColor =
    cardType === 'soul_perspective' ? 'rgba(100,160,255,' :
    cardType === 'reframe_invitation' ? 'rgba(100,200,140,' :
    cardType === 'message_within' ? 'rgba(220,120,150,' :
    'rgba(155,114,192,'

  return (
    <div className="flex items-center justify-center mb-8">
      <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
        <circle cx="60" cy="60" r="56" stroke={`${strokeColor}0.15)`} strokeWidth="1" />
        <circle cx="60" cy="60" r="42" stroke={`${strokeColor}0.2)`} strokeWidth="1" />
        <circle cx="60" cy="60" r="28" stroke={`${strokeColor}0.28)`} strokeWidth="1" />
        <circle cx="60" cy="60" r="10" fill={accentColor} />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => {
          const rad = (deg * Math.PI) / 180
          const x = 60 + 35 * Math.cos(rad)
          const y = 60 + 35 * Math.sin(rad)
          return (
            <ellipse
              key={i}
              cx={x} cy={y}
              rx="6" ry="10"
              transform={`rotate(${deg + 90}, ${x}, ${y})`}
              fill={`${strokeColor}0.12)`}
              stroke={`${strokeColor}0.22)`}
              strokeWidth="0.8"
            />
          )
        })}
        <circle cx="60" cy="22" r="3" fill="rgba(212,175,100,0.4)" />
        <circle cx="98" cy="60" r="2" fill="rgba(212,175,100,0.3)" />
        <circle cx="60" cy="98" r="2.5" fill="rgba(212,175,100,0.35)" />
        <circle cx="22" cy="60" r="2" fill="rgba(212,175,100,0.3)" />
      </svg>
    </div>
  )
}

const REGULATION_PRACTICES = [
  'Place one hand on your heart or belly. Take three slow breaths — in for 4 counts, out for 6.',
  'Feel your feet on the ground and notice the support beneath you.',
  'Two quick inhales through the nose, then one long exhale.',
]

function buildMessages(
  cardType: InsightCardType,
  storyText: string,
  bodyLocation: string,
  reminderText: string,
  patternReflection: string,
  reframeResponse: string,
): SessionMessage[] {
  if (cardType === 'pattern_archetype') {
    return [
      { role: 'user', content: `Story: "${storyText}". Body: "${bodyLocation}". Memory: "${reminderText}".` },
      { role: 'assistant', content: `Pattern named: "${patternReflection}".` },
      { role: 'user', content: '[Generate insight card]' },
    ]
  }
  if (cardType === 'soul_perspective' || cardType === 'reframe_invitation') {
    return [
      { role: 'user', content: `Pattern: "${patternReflection}". Response to reframe: "${reframeResponse}". [Generate insight card]` },
    ]
  }
  // message_within
  return [
    { role: 'user', content: `Pattern: "${patternReflection}". Shift explored: "${reframeResponse}". [Generate insight card]` },
  ]
}

export default function InsightCard({
  cardType = 'pattern_archetype',
  sessionId,
  userId = '',
  emotion,
  intensity,
  firstName = '',
  intentionText = '',
  storyText = '',
  bodyLocation = '',
  reminderText = '',
  patternReflection = '',
  reframeResponse = '',
  seriesContext,
  onComplete,
}: InsightCardProps) {
  const { t, i18n } = useTranslation()
  // soul_perspective and message_within skip the pre-regulation breath screen
  const [uiState, setUiState] = useState<UIState>(
    cardType === 'soul_perspective' || cardType === 'message_within' ? 'generating' : 'pre-regulation'
  )
  useScreenId(`${CARD_HOST[cardType]}.card.${CARD_SCREEN[uiState]}`)
  // Back: the card's own screens, in order. The card is hosted by a phase (See,
  // Shift or Choose), so it is asked before that phase is, and Back leaves the
  // card only from its first screen. "Generating" is the wait for the card text
  // and is never a place to return to.
  useScreenTrail<UIState>({
    screen: uiState,
    go: setUiState,
    kind: s => (s === 'generating' ? 'loading' : s === 'error' ? 'error' : 'rest'),
    depth: BACK_DEPTH.hosted,
  })
  const [cardTitle, setCardTitle] = useState('')
  const [cardMessage, setCardMessage] = useState('')
  const [integrationText, setIntegrationText] = useState('')
  // Why the card could not be written, as a code the error screen translates
  // (lib/claude.ts). Set when the call fails; cleared by Try again.
  const [errorCode, setErrorCode] = useState<string | null>(null)
  // Which attempt is current, so a slow answer to an attempt that has since been
  // retried cannot overwrite the newer one.
  const attempt = useRef(0)
  // wasSaved drives the "saved to your journal" confirmations (display + integration).
  const [wasSaved, setWasSaved] = useState(false)
  // Guard so the auto-save insert runs at most once per card.
  const autoSavedRef = useRef(false)
  // Rendering/sharing the card as an image (the shareable Insight Card artifact)
  const [sharing, setSharing] = useState(false)
  // Preview of the rendered card image — shown in an overlay so the user can SEE
  // the graphic before saving/sharing it (the canvas itself is kept in a ref).
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const cardCanvasRef = useRef<HTMLCanvasElement | null>(null)
  // The image preview is laid over the card: Back closes it first.
  useSessionBack(() => {
    if (!previewUrl) return 'first'
    setPreviewUrl(null)
    return 'handled'
  }, BACK_DEPTH.overlay)

  const meta = CARD_META[cardType]
  // Regulation practices read from the locale, length-guarded against the English list.
  const regulationPractices = (() => {
    const v = t('insight.regulationPractices', { returnObjects: true })
    return Array.isArray(v) && v.length === REGULATION_PRACTICES.length ? (v as string[]) : REGULATION_PRACTICES
  })()

  /** Ask the guide to write the card. Called when the card opens, and again by Try again. */
  function generate() {
    const mine = ++attempt.current
    const msgs = buildMessages(cardType, storyText, bodyLocation, reminderText, patternReflection, reframeResponse)
    callClaude({ phase: meta.phase, emotion, intensity, messages: msgs, firstName, intentionText, sessionId, seriesContext })
      .then(result => {
        if (mine !== attempt.current) return
        if ('error' in result) { setErrorCode(errorCodeOf(result)); return }
        const { title, message } = parseCard(result.response)
        setCardTitle(title)
        setCardMessage(message)
      })
  }

  useEffect(() => {
    generate()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A failed call used to leave the "card arriving" spinner up for ever: the
  // error state was drawn but nothing ever set it (#70). When the call has failed
  // and the person is waiting on the card, show the error screen. If they are
  // still on the breathing screen they see it when they ask for the card, below.
  useEffect(() => {
    if (errorCode && uiState === 'generating') setUiState('error')
  }, [errorCode, uiState])

  // When the card is written while the person is waiting for it, show it after a
  // short pause so the arrival does not flash. This was a setTimeout started
  // from inside render, which is a side effect in render and could fire more than
  // once; an effect with a cleanup starts it once and cancels it if they leave.
  useEffect(() => {
    if (uiState !== 'generating' || !cardTitle) return
    const timer = setTimeout(() => setUiState('display'), 600)
    return () => clearTimeout(timer)
  }, [uiState, cardTitle])

  /** Where "ready to receive" and "already calm" lead: the card if it is written, the error if it failed, else the wait. */
  function receiveCard() {
    setUiState(cardTitle ? 'display' : errorCode ? 'error' : 'generating')
  }

  function tryAgain() {
    setErrorCode(null)
    setUiState('generating')
    generate()
  }

  // Auto-save every card to the journal the moment it's generated, so a card is
  // never lost (the user no longer has to remember to save it). Runs once.
  // Anonymous / not-yet-authed users have no journal — skip the insert but still
  // show the card. The DB insert is fire-and-forget; on failure wasSaved stays
  // false so we never falsely claim it was saved.
  useEffect(() => {
    if (autoSavedRef.current || !cardTitle || !cardMessage) return
    autoSavedRef.current = true
    void (async () => {
      const uid = userId || (await supabase!.auth.getUser()).data.user?.id || ''
      if (!uid) return
      const { error } = await db()
        .from('insight_cards')
        .insert({
          session_id: sessionId,
          user_id: uid,
          card_type: cardType,
          card_title: cardTitle,
          card_message: cardMessage,
          saved: true,
        })
      if (!error) setWasSaved(true)
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardTitle, cardMessage])

  // Build the card image and show it in a preview overlay (no download yet) so
  // the user actually sees the graphic. Surfaces only the cleared card content —
  // never the user's raw story text or an event-reframe (clinical copy rule).
  function openCardPreview() {
    if (!cardTitle || !cardMessage) return
    // Pull the two gradient stops out of the CSS gradient string for the canvas.
    const stops = meta.gradient.match(/#[0-9A-Fa-f]{6}/g) || ['#3D1F62', '#6B3FA0']
    const canvas = buildInsightCardCanvas({
      cardLabel: t(`journal.cardTypes.${cardType}`),
      cardTitle,
      cardMessage,
      gradientFrom: stops[0],
      gradientTo: stops[1] || stops[0],
      emotion,
      intensity,
      date: new Date().toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' }),
    })
    cardCanvasRef.current = canvas
    setPreviewUrl(canvas.toDataURL('image/png'))
  }

  // From the preview: deliver the image via the native share sheet (mobile) or
  // a download (desktop).
  async function shareCardImage() {
    if (sharing || !cardCanvasRef.current) return
    setSharing(true)
    try {
      const date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      await deliverPng(
        cardCanvasRef.current,
        `insight-card-${date.replace(/,?\s/g, '-').toLowerCase()}.png`,
        t('insight.shareText', { title: cardTitle }),
      )
    } finally {
      setSharing(false)
    }
  }

  function handleReceive() {
    setUiState('integration')
  }

  if (uiState === 'error') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 page-enter">
        <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>{t('errors.somethingWrong')}</p>
        {errorCode && (
          <p className="text-xs mb-4 text-center max-w-xs" style={{ color: '#6B5F7A' }}>{t(guideErrorKey(errorCode))}</p>
        )}
        <button
          onClick={tryAgain}
          style={{ background: '#3D1F62', color: 'white', border: 'none', borderRadius: '9999px', padding: '0.75rem 1.5rem', cursor: 'pointer', fontSize: '0.875rem' }}
        >
          {t('errors.tryAgain')}
        </button>
        <button
          onClick={onComplete}
          className="mt-3"
          style={{ background: 'transparent', color: '#6B5F7A', border: 'none', cursor: 'pointer', fontSize: '0.875rem' }}
        >
          {t('insight.continueWithout')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col page-enter">
      <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

        {/* ── Screen 1: Pre-Card Regulation ───────────────────────────────── */}
        {uiState === 'pre-regulation' && (
          <div className="flex-1 flex flex-col message-appear">
            <p className="text-xs font-medium uppercase tracking-widest mb-8" style={{ color: '#9B72C0' }}>
              {t('insight.beforeWeContinue')}
            </p>

            <p className="font-display text-xl leading-snug mb-2" style={{ color: '#1C1128', fontWeight: 400 }}>
              {t('insight.cardWaiting')}
            </p>
            <p className="text-sm leading-relaxed mb-8" style={{ color: '#6B5F7A' }}>
              {t('insight.gentleMoment')}
            </p>

            <BreathCircle />

            <p className="text-sm font-medium mb-4" style={{ color: '#6B5F7A' }}>
              {t('insight.choosePractice')}
            </p>
            <div className="space-y-3 mb-8">
              {regulationPractices.map((p, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: '#9B72C0' }} />
                  <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{p}</p>
                </div>
              ))}
            </div>

            <div className="flex-1" />
            <button
              onClick={receiveCard}
              className="w-full py-4 rounded-xl font-medium tracking-wide"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('insight.readyToReceive')}
            </button>
            <button
              onClick={receiveCard}
              className="w-full py-3 rounded-xl text-sm mt-2"
              style={{ background: 'transparent', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
            >
              {t('insight.alreadyCalm')}
            </button>
          </div>
        )}

        {/* ── Generating (brief wait if card not ready) ───────────────────── */}
        {uiState === 'generating' && (
          <div className="flex-1 flex flex-col items-center justify-center">
            <LoadingSpiral size={28} label={t('insight.cardArriving')} />
          </div>
        )}

        {/* ── Screen 3: Display ───────────────────────────────────────────── */}
        {uiState === 'display' && (
          <div className="flex-1 flex flex-col message-appear">

            <CardIllustration cardType={cardType} />

            <div
              className="w-full rounded-2xl px-7 py-8 mb-8 text-center"
              style={{
                background: meta.gradient,
                boxShadow: '0 8px 40px rgba(61,31,98,0.3)',
              }}
            >
              <p className="text-xs font-medium uppercase tracking-widest mb-5"
                style={{ color: 'rgba(255,255,255,0.45)' }}>
                {t(`journal.cardTypes.${cardType}`)}
              </p>
              <p className="font-display text-xl leading-snug mb-5"
                style={{ color: 'white', fontStyle: 'italic', fontWeight: 400 }}>
                {cardTitle}
              </p>
              <div style={{ width: '32px', height: '1px', background: 'rgba(255,255,255,0.18)', margin: '0 auto 1.25rem' }} />
              <p className="text-sm leading-relaxed"
                style={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.7 }}>
                {cardMessage}
              </p>
            </div>

            <div className="flex-1" />

            <div className="flex flex-col gap-3">
              <button
                onClick={handleReceive}
                className="w-full py-4 rounded-xl font-medium tracking-wide"
                style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                {t('insight.receiveMessage')}
              </button>
              {wasSaved && (
                <p className="text-xs text-center" style={{ color: '#9B72C0' }}>
                  {t('insight.savedFindAnytime')}
                </p>
              )}
              <button
                onClick={openCardPreview}
                className="w-full py-3 rounded-xl text-sm font-medium"
                style={{ background: 'rgba(155,114,192,0.12)', border: '1.5px solid #C4B5D4', color: '#3D1F62', cursor: 'pointer' }}
              >
                {t('insight.seeShare')}
              </button>
              <p className="text-xs text-center leading-relaxed" style={{ color: '#C4B5D4', marginTop: '-0.25rem' }}>
                {t('insight.thisIsYours')}
              </p>
              <button
                onClick={onComplete}
                className="w-full py-3 rounded-xl text-sm"
                style={{ background: 'transparent', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
              >
                {t('session.continue')}
              </button>
            </div>

            {/* Preview overlay — shows the actual card graphic so the user can see
                it, then save/share. On desktop "Save / share" downloads the PNG;
                on mobile it opens the native share sheet. */}
            {previewUrl && (
              <div
                onClick={() => setPreviewUrl(null)}
                style={{
                  position: 'fixed', inset: 0, zIndex: 50,
                  background: 'rgba(28,17,40,0.88)', backdropFilter: 'blur(4px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
                }}
              >
                <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', width: '100%', maxWidth: '20rem' }}>
                  <img
                    src={previewUrl}
                    alt={t('insight.yourCardAlt')}
                    style={{ width: '100%', maxHeight: '64vh', objectFit: 'contain', borderRadius: '1rem', boxShadow: '0 12px 48px rgba(0,0,0,0.45)' }}
                  />
                  <button
                    onClick={shareCardImage}
                    disabled={sharing}
                    className="w-full py-3 rounded-xl font-medium"
                    style={{ background: 'white', color: '#3D1F62', border: 'none', cursor: sharing ? 'not-allowed' : 'pointer' }}
                  >
                    {sharing ? t('insight.preparing') : t('insight.saveShare')}
                  </button>
                  <p className="text-xs text-center" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    {t('insight.thisIsYours')}
                  </p>
                  <button
                    onClick={() => setPreviewUrl(null)}
                    className="text-sm"
                    style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.55)', cursor: 'pointer' }}
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Screen 3: Integration (card stays visible) ───────────────────── */}
        {uiState === 'integration' && (
          <div className="flex-1 flex flex-col message-appear">
            <p className="text-xs font-medium uppercase tracking-widest mb-5" style={{ color: '#9B72C0' }}>
              {t('insight.integration')}
            </p>

            {/* Card remains visible during integration */}
            <div
              className="w-full rounded-2xl px-6 py-6 mb-6 text-center"
              style={{
                background: meta.gradient,
                boxShadow: '0 4px 20px rgba(61,31,98,0.2)',
              }}
            >
              <p className="text-xs font-medium uppercase tracking-widest mb-3"
                style={{ color: 'rgba(255,255,255,0.45)' }}>
                {t(`journal.cardTypes.${cardType}`)}
              </p>
              <p className="font-display text-base leading-snug mb-3"
                style={{ color: 'white', fontStyle: 'italic', fontWeight: 400 }}>
                {cardTitle}
              </p>
              <div style={{ width: '28px', height: '1px', background: 'rgba(255,255,255,0.18)', margin: '0 auto 0.875rem' }} />
              <p className="text-sm leading-relaxed"
                style={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.65 }}>
                {cardMessage}
              </p>
            </div>

            <p className="text-sm leading-relaxed mb-4" style={{ color: '#6B5F7A' }}>
              {t(`insight.cards.${cardType}.integrationQuestion`)}
            </p>

            <textarea
              value={integrationText}
              onChange={e => setIntegrationText(e.target.value)}
              placeholder={t('insight.writeFewWords')}
              rows={3}
              maxLength={1000}
              className="w-full px-4 py-4 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
              style={{ borderColor: '#C4B5D4', background: 'white', color: '#1C1128' }}
              onFocus={e => { e.target.style.borderColor = '#9B72C0'; e.target.style.boxShadow = '0 0 0 3px rgba(155,114,192,0.12)' }}
              onBlur={e => { e.target.style.borderColor = '#C4B5D4'; e.target.style.boxShadow = 'none' }}
            />

            {/* Persistent save confirmation — so the user knows where their card went */}
            {wasSaved && (
              <p className="text-xs mt-3 leading-relaxed" style={{ color: '#9B72C0' }}>
                {t('insight.savedFind')}
              </p>
            )}

            <p className="text-xs mt-3 mb-6 leading-relaxed" style={{ color: '#C4B5D4' }}>
              {t('insight.breatheGently')}
            </p>

            <div className="flex-1" />
            <button
              onClick={onComplete}
              className="w-full py-4 rounded-xl font-medium tracking-wide"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('session.continue')}
            </button>
          </div>
        )}

      </div>
    </div>
  )
}
